import type { SearchPosting, StatedPay } from "@/lib/api/catalogue";
import { formatCount } from "@/lib/format";

/**
 * Pay as a posting states it, in words. Pure.
 */

/**
 * A sum of money in its currency, whole: "$180,000". Compact, for a chip:
 * "$180k", "$182.5k", "$1.3M", with sums under a thousand left whole.
 */
export function formatMoney(value: number, currency: string, compact = false): string {
    try {
        const words = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            ...(compact
                ? // The minimum too: Node 22's Intl keeps a currency's
                  // trailing zero ("$190.0K") without it.
                  { notation: "compact", minimumFractionDigits: 0, maximumFractionDigits: 1 }
                : { maximumFractionDigits: 0 }),
        }).format(value);
        return compact ? words.replace(/(\d)K/, "$1k") : words;
    } catch {
        return `${formatCount(value)} ${currency}`;
    }
}

/** A year goes without saying; any other period is named, shortly. */
const INTERVAL_WORD: Record<string, string> = {
    YEAR: "",
    MONTH: "/mo",
    WEEK: "/wk",
    DAY: "/day",
    HOUR: "/hr",
};

/** The pay a posting states, from its own field or else the model's reading. */
export function statedPay(posting: SearchPosting): StatedPay | null {
    const pay = posting.pay ?? posting.reading?.pay ?? null;
    return pay && (pay.min !== null || pay.max !== null) ? pay : null;
}

/**
 * "$190,000 to $230,000", "$182,000", "$25/hr", or null when nothing
 * is stated. The period is the one the catalogue gives, and figures with
 * none stand alone.
 * Compact, the sums read as `formatMoney` shortens them: "$190k to $230k".
 */
export function payLabel(pay: StatedPay | null, compact = false): string | null {
    if (!pay) return null;
    const first = pay.min ?? pay.max;
    const second = pay.max ?? pay.min;
    if (first === null || second === null) return null;
    // A board, or the model's reading, may give the range high to low.
    const low = Math.min(first, second);
    const high = Math.max(first, second);
    const range =
        low === high
            ? formatMoney(low, pay.currency, compact)
            : `${formatMoney(low, pay.currency, compact)} to ${formatMoney(high, pay.currency, compact)}`;
    const period = pay.period ?? pay.interval ?? "";
    return `${range}${Object.hasOwn(INTERVAL_WORD, period) ? INTERVAL_WORD[period] : ""}`;
}
